import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  MessageSquare, 
  Send, 
  Phone, 
  MapPin, 
  AlertCircle, 
  CheckCircle, 
  HelpCircle, 
  MessageCircle, 
  Flag, 
  ThumbsUp,
  Sparkles,
  Clock,
  Home,
  Globe,
  Building2,
  User,
  AtSign,
  FileText,
  Zap,
  ArrowRight,
  Star,
  Shield,
  Heart,
  Loader2
} from 'lucide-react';

// Animaciones
const fadeInUp = {
  hidden: { 
    opacity: 0, 
    y: 30,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const slideInLeft = {
  hidden: { opacity: 0, x: -20 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }
  }
};

const ContactPage = () => {
  // Form state
  const [formState, setFormState] = useState({
    nombre: '',
    email: '',
    telefono: '',
    tipo: 'consulta',
    asunto: '',
    mensaje: ''
  });

  // Validation errors
  const [errors, setErrors] = useState({});
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState({ success: false, message: '' });

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formState.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!formState.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      newErrors.email = 'Por favor ingrese un email válido';
    }
    if (!formState.asunto.trim()) newErrors.asunto = 'El asunto es requerido';
    if (!formState.mensaje.trim()) {
      newErrors.mensaje = 'El mensaje es requerido';
    } else if (formState.mensaje.trim().length < 20) {
      newErrors.mensaje = 'El mensaje debe tener al menos 20 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Preparar los datos del formulario para el envío
      const contactData = {
        nombre: formState.nombre,
        email: formState.email,
        telefono: formState.telefono || 'No proporcionado',
        tipo_mensaje: formState.tipo,
        asunto: formState.asunto,
        mensaje: formState.mensaje
      };
      
      // Usar el nuevo endpoint
      const response = await fetch('https://backend-production-1786.up.railway.app/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al enviar el mensaje');
      }
      
      const responseData = await response.json();
      
      // Mostrar mensaje de éxito
      setSubmitResult({
        success: true,
        message: 'Tu mensaje ha sido enviado correctamente. Te hemos enviado un correo de confirmación.'
      });
      
      // Reset form
      setFormState({
        nombre: '',
        email: '',
        telefono: '',
        tipo: 'consulta',
        asunto: '',
        mensaje: ''
      });
      
    } catch (error) {
      console.error('Error al enviar el mensaje:', error);
      
      // Handle error
      setSubmitResult({
        success: false,
        message: error.message || 'Hubo un error al enviar tu mensaje. Por favor intenta nuevamente.'
      });
    } finally {
      setIsSubmitting(false);
      
      // Scroll to top to show result message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Get icon for message type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'consulta': return <HelpCircle className="w-5 h-5" />;
      case 'retroalimentacion': return <ThumbsUp className="w-5 h-5" />;
      case 'reporte': return <Flag className="w-5 h-5" />;
      case 'otro': return <MessageCircle className="w-5 h-5" />;
      default: return <HelpCircle className="w-5 h-5" />;
    }
  };

  // Get color for message type
  const getTypeColors = (type) => {
    const colorMap = {
      consulta: {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-700',
        text: 'text-blue-700 dark:text-blue-300',
        icon: 'text-blue-600 dark:text-blue-400'
      },
      retroalimentacion: {
        bg: 'bg-green-50 dark:bg-green-900/20',
        border: 'border-green-200 dark:border-green-700',
        text: 'text-green-700 dark:text-green-300',
        icon: 'text-green-600 dark:text-green-400'
      },
      reporte: {
        bg: 'bg-red-50 dark:bg-red-900/20',
        border: 'border-red-200 dark:border-red-700',
        text: 'text-red-700 dark:text-red-300',
        icon: 'text-red-600 dark:text-red-400'
      },
      otro: {
        bg: 'bg-purple-50 dark:bg-purple-900/20',
        border: 'border-purple-200 dark:border-purple-700',
        text: 'text-purple-700 dark:text-purple-300',
        icon: 'text-purple-600 dark:text-purple-400'
      }
    };
    
    return colorMap[type] || colorMap.consulta;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-100/50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-blue-100 to-transparent dark:from-blue-900/30 dark:to-transparent opacity-40"></div>
        <div className="absolute bottom-0 right-0 w-full h-64 bg-gradient-to-t from-pink-100 to-transparent dark:from-purple-900/30 dark:to-transparent opacity-40"></div>
        
        {/* Decorative circles */}
        <div className="absolute top-20 left-20 w-32 h-32 rounded-full bg-blue-200 dark:bg-blue-900/30 opacity-20 blur-xl"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 rounded-full bg-purple-200 dark:bg-purple-900/30 opacity-20 blur-xl"></div>
        <div className="absolute top-1/3 right-1/4 w-24 h-24 rounded-full bg-emerald-200 dark:bg-emerald-900/30 opacity-20 blur-xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header moderno */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex justify-center mb-6"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 dark:from-blue-500 dark:to-indigo-500 rounded-3xl flex items-center justify-center shadow-2xl">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4"
          >
            Contáctanos
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto"
          >
            Estamos aquí para ayudarte en cada paso de tu experiencia inmobiliaria. 
            Envíanos tus dudas, comentarios o sugerencias.
          </motion.p>
        </motion.div>

        {/* Success/Error Message */}
        <AnimatePresence>
          {submitResult.message && (
            <motion.div 
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              className={`mb-8 rounded-2xl shadow-lg border-l-4 ${
                submitResult.success 
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400' 
                  : 'bg-red-50 dark:bg-red-900/20 border-red-500 dark:border-red-400'
              }`}
            >
              <div className="p-6">
                <div className="flex gap-4 items-start">
                  {submitResult.success ? 
                    <CheckCircle className="w-8 h-8 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" /> : 
                    <AlertCircle className="w-8 h-8 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  }
                  <div className="flex-1">
                    <h3 className={`font-semibold text-xl mb-2 ${
                      submitResult.success ? 'text-green-800 dark:text-green-100' : 'text-red-800 dark:text-red-100'
                    }`}>
                      {submitResult.success ? '¡Mensaje enviado exitosamente!' : 'Error al enviar mensaje'}
                    </h3>
                    <p className={`${
                      submitResult.success ? 'text-green-700 dark:text-green-200' : 'text-red-700 dark:text-red-200'
                    }`}>
                      {submitResult.message}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div 
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl shadow-2xl dark:shadow-black/30 overflow-hidden border border-gray-200/50 dark:border-gray-700/50"
        >
          <div className="flex flex-col lg:flex-row">
            {/* Contact Information mejorado */}
            <motion.div 
              variants={slideInLeft}
              initial="hidden"
              animate="visible"
              className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-700 dark:via-indigo-700 dark:to-purple-700 p-8 lg:p-12 text-white lg:w-2/5 relative overflow-hidden"
            >
              {/* Decorative elements */}
              <div className="absolute inset-0 bg-black/10 dark:bg-black/20"></div>
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-20 translate-x-20"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-16 -translate-x-16"></div>
              
              <div className="relative z-10">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-center gap-3 mb-8"
                >
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/30">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">InKayros</h2>
                    <p className="text-blue-100">Tu hogar ideal te espera</p>
                  </div>
                </motion.div>
                
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mb-10 text-blue-100 text-lg leading-relaxed"
                >
                  ¿Tienes alguna pregunta o necesitas ayuda? Nuestro equipo de expertos inmobiliarios 
                  está listo para asistirte en cada paso de tu búsqueda.
                </motion.p>
                
                <motion.div 
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                  className="space-y-6 mb-10"
                >
                  {[
                    { icon: Mail, title: 'Email', info: 'contacto@inkayros.com', color: 'bg-blue-500/30' },
                    { icon: Globe, title: 'Sitio web', info: 'www.inkayros.com', color: 'bg-purple-500/30' }
                  ].map((item, index) => (
                    <motion.div 
                      key={index}
                      variants={fadeInUp}
                      className="flex items-start gap-4"
                    >
                      <div className={`${item.color} p-4 rounded-2xl backdrop-blur-sm border border-white/20`}>
                        <item.icon className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{item.title}</h3>
                        <p className="text-blue-100 mt-1">{item.info}</p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
                
                <div className="border-t border-blue-500/30 pt-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Clock className="w-6 h-6 text-blue-200" />
                    <h3 className="font-semibold text-lg">Horario de Atención</h3>
                  </div>
                  <div className="space-y-3 text-blue-100">
                    <div className="flex justify-between items-center">
                      <span>Lunes a Viernes</span>
                      <span className="font-medium">9:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Sábados</span>
                      <span className="font-medium">9:00 AM - 1:00 PM</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Domingos</span>
                      <span className="font-medium text-blue-300">Cerrado</span>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="mt-8 grid grid-cols-3 gap-4">
                  {[
                    { number: '24/7', label: 'Soporte online' },
                    { number: '1k+', label: 'Clientes felices' },
                    { number: '99%', label: 'Satisfacción' }
                  ].map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className="text-2xl font-bold text-white">{stat.number}</div>
                      <div className="text-xs text-blue-200 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
            
            {/* Contact Form mejorado */}
            <div className="p-8 lg:p-12 lg:w-3/5">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mb-8"
              >
                <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 dark:from-blue-400 dark:to-indigo-400 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  Envíanos un mensaje
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Completa el formulario y te responderemos lo antes posible
                </p>
              </motion.div>
              
              <div className="space-y-8">
                {/* Name */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                >
                  <label htmlFor="nombre" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Nombre completo <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <input
                    id="nombre"
                    name="nombre"
                    type="text"
                    value={formState.nombre}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-4 rounded-2xl border-2 transition-all duration-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${
                      errors.nombre 
                        ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20' 
                        : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/20'
                    }`}
                    placeholder="Ingresa tu nombre completo"
                  />
                  {errors.nombre && (
                    <motion.p 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1"
                    >
                      <AlertCircle className="w-4 h-4" />
                      {errors.nombre}
                    </motion.p>
                  )}
                </motion.div>
                
                {/* Email and Phone */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      <div className="flex items-center gap-2">
                        <AtSign className="w-4 h-4" />
                        Email <span className="text-red-500">*</span>
                      </div>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formState.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-4 rounded-2xl border-2 transition-all duration-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${
                        errors.email 
                          ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20' 
                          : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/20'
                      }`}
                      placeholder="tu@email.com"
                    />
                    {errors.email && (
                      <motion.p 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1"
                      >
                        <AlertCircle className="w-4 h-4" />
                        {errors.email}
                      </motion.p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="telefono" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        Teléfono <span className="text-gray-400 dark:text-gray-500 font-normal">(opcional)</span>
                      </div>
                    </label>
                    <input
                      id="telefono"
                      name="telefono"
                      type="tel"
                      value={formState.telefono}
                      onChange={handleInputChange}
                      className="w-full px-4 py-4 rounded-2xl border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/20 transition-all duration-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      placeholder="+51 123 456 789"
                    />
                  </div>
                </motion.div>
                
                {/* Message Type */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                >
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4" />
                      Tipo de mensaje
                    </div>
                  </label>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                      { id: 'consulta', label: 'Consulta' },
                      { id: 'retroalimentacion', label: 'Retroalimentación' },
                      { id: 'reporte', label: 'Reporte' },
                      { id: 'otro', label: 'Otro' }
                    ].map(type => {
                      const colors = getTypeColors(type.id);
                      const isSelected = formState.tipo === type.id;
                      
                      return (
                        <motion.button
                          key={type.id}
                          type="button"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setFormState(prev => ({ ...prev, tipo: type.id }))}
                          className={`flex items-center justify-center gap-2 px-4 py-4 rounded-2xl border-2 transition-all duration-200 ${
                            isSelected
                              ? `${colors.bg} ${colors.border} ${colors.text} font-semibold shadow-lg`
                              : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-800'
                          }`}
                        >
                          <div className={isSelected ? colors.icon : 'text-gray-400 dark:text-gray-500'}>
                            {getTypeIcon(type.id)}
                          </div>
                          <span className="text-sm">{type.label}</span>
                        </motion.button>
                      );
                    })}
                  </div>
                </motion.div>
                
                {/* Subject */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.0 }}
                >
                  <label htmlFor="asunto" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Asunto <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <input
                    id="asunto"
                    name="asunto"
                    type="text"
                    value={formState.asunto}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-4 rounded-2xl border-2 transition-all duration-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${
                      errors.asunto 
                        ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20' 
                        : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/20'
                    }`}
                    placeholder="Escribe el asunto de tu mensaje"
                  />
                  {errors.asunto && (
                    <motion.p 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1"
                    >
                      <AlertCircle className="w-4 h-4" />
                      {errors.asunto}
                    </motion.p>
                  )}
                </motion.div>
                
                {/* Message */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1 }}
                >
                  <label htmlFor="mensaje" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Mensaje <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    rows="6"
                    value={formState.mensaje}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-4 rounded-2xl border-2 transition-all duration-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none ${
                      errors.mensaje 
                        ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20' 
                        : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/20'
                    }`}
                    placeholder="Escribe tu mensaje aquí..."
                  ></textarea>
                  <div className="flex justify-between items-center mt-2">
                    {errors.mensaje && (
                      <motion.p 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1"
                      >
                        <AlertCircle className="w-4 h-4" />
                        {errors.mensaje}
                      </motion.p>
                    )}
                    <p className="text-sm text-gray-500 dark:text-gray-400 ml-auto">
                      <span className={formState.mensaje.length < 20 ? 'text-gray-400 dark:text-gray-500' : 'text-green-600 dark:text-green-400'}>
                        {formState.mensaje.length}
                      </span>
                      <span className="text-gray-400 dark:text-gray-500">/20 caracteres mínimo</span>
                    </p>
                  </div>
                </motion.div>
                
                {/* Submit Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                >
                  <motion.button
                    type="button"
                    disabled={isSubmitting}
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                    onClick={handleSubmit}
                    className={`w-full flex items-center justify-center gap-3 px-8 py-4 font-semibold rounded-2xl transition-all duration-200 ${
                      isSubmitting 
                        ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-500 dark:to-indigo-500 hover:from-blue-700 hover:to-indigo-700 dark:hover:from-blue-600 dark:hover:to-indigo-600 shadow-lg hover:shadow-xl'
                    } text-white`}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-6 w-6 animate-spin" />
                        <span>Enviando mensaje...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-6 w-6" />
                        <span>Enviar mensaje</span>
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </motion.button>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* FAQ Section mejorado */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3 }}
          className="mt-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">Preguntas Frecuentes</h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">Encuentra respuestas rápidas a las consultas más comunes</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                question: "¿Cómo puedo publicar una propiedad?",
                answer: "Para publicar una propiedad, primero debes registrarte como vendedor, socio inmobiliaria o constructora. Luego, desde tu panel de control, podrás crear y gestionar tus publicaciones.",
                icon: Home,
                color: 'blue'
              },
              {
                question: "¿Hay costo por registrarme en la plataforma?",
                answer: "El registro en InKayros es completamente gratuito tanto para clientes como para vendedores. La primera publicación es grátis, sin embargo, tanto las siguientes publicaciones como algunas otras funciones premium tienen costo adicional.",
                icon: Star,
                color: 'emerald'
              },
              {
                question: "¿Cómo puedo contactar a un vendedor?",
                answer: "Puedes contactar a un vendedor directamente desde la página de detalle de la propiedad que te interesa, usando el formulario de contacto o los datos disponibles.",
                icon: MessageCircle,
                color: 'purple'
              },
              {
                question: "¿Puedo eliminar mi cuenta?",
                answer: "Sí, puedes eliminar tu cuenta en cualquier momento desde la configuración de tu perfil o contactando con nuestro soporte. Ten en cuenta que todas tus publicaciones serán eliminadas.",
                icon: Shield,
                color: 'red'
              }
            ].map((faq, index) => {
              const colors = {
                blue: 'from-blue-500 to-cyan-500 dark:from-blue-400 dark:to-cyan-400',
                emerald: 'from-emerald-500 to-green-500 dark:from-emerald-400 dark:to-green-400',
                purple: 'from-purple-500 to-pink-500 dark:from-purple-400 dark:to-pink-400',
                red: 'from-red-500 to-orange-500 dark:from-red-400 dark:to-orange-400'
              };
              
              return (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -4 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-black/20 border border-gray-200 dark:border-gray-700 p-8 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${colors[faq.color]} rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0`}>
                      <faq.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100 mb-3">
                        {faq.question}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mt-12"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-500 dark:to-indigo-500 text-white font-medium rounded-2xl hover:shadow-lg transition-all duration-200"
            >
              <span>Ver todas las preguntas frecuentes</span>
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Trust indicators */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5 }}
          className="mt-20 text-center"
        >
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 rounded-3xl p-8 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Heart className="w-6 h-6 text-red-500" />
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Compromiso con nuestros usuarios</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              En InKayros nos comprometemos a brindarte la mejor experiencia inmobiliaria. 
              Tu satisfacción y confianza son nuestra prioridad número uno.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactPage;